<template>
  <div class="propeller-address-card">
    <template v-if="showCard">
      <div
        class="propeller-address-card__card bg-card p-4 rounded-[var(--radius-container)] shadow-sm border border-border h-full flex flex-col"
        :data-default="addr?.isDefault === 'Y' ? 'true' : 'false'"
        :data-type="addr?.type || ''"
      >
        <div class="propeller-address-card__body flex-grow">
          <slot
            v-if="addressType && showTypeBadge !== false"
            name="typeBadge"
            :address="addr"
            :addressType="addressType"
            :addressTypeLabel="getLabel('addressType:' + addressType, addressType)"
          >
            <span
              class="propeller-address-card__type-badge inline-block rounded bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground mb-2"
            >
              {{ getLabel('addressType:' + addressType, addressType) }}
            </span>
          </slot>

          <template v-if="showCompanyName !== false && addr?.company">
            <div class="propeller-address-card__company font-bold text-lg mb-1">
              {{ addr?.company }}
            </div>
          </template>

          <slot
            v-if="showFullName !== false && (addr?.firstName || addr?.lastName)"
            name="name"
            :address="addr"
            :fullName="[
              props.showSalutation !== false
                ? addr?.gender === 'M'
                  ? 'Mr.'
                  : addr?.gender === 'F'
                    ? 'Mrs.'
                    : null
                : null,
              addr?.firstName,
              addr?.middleName,
              addr?.lastName,
            ].filter(Boolean).join(' ')"
            :salutation="addr?.gender"
          >
            <div class="propeller-address-card__name font-medium mb-1">
              {{
                [
                  props.showSalutation !== false
                    ? addr?.gender === "M"
                      ? "Mr."
                      : addr?.gender === "F"
                        ? "Mrs."
                        : null
                    : null,
                  addr?.firstName,
                  addr?.middleName,
                  addr?.lastName,
                ]
                  .filter(Boolean)
                  .join(" ")
              }}
            </div>
          </slot>

          <slot
            name="addressLines"
            :address="addr"
            :streetLine="[
              addr?.street,
              showNumberExtension !== false ? addr?.number : null,
              showNumberExtension !== false ? addr?.numberExtension : null,
            ].filter(Boolean).join(' ')"
            :cityLine="[
              showPostalCode !== false ? addr?.postalCode : null,
              showCity !== false ? addr?.city : null,
            ].filter(Boolean).join(' ')"
          >
            <template v-if="showStreet !== false && addr?.street">
              <div class="propeller-address-card__street text-muted-foreground">
                {{
                  [
                    addr?.street,
                    showNumberExtension !== false ? addr?.number : null,
                    showNumberExtension !== false ? addr?.numberExtension : null,
                  ]
                    .filter(Boolean)
                    .join(" ")
                }}
              </div>
            </template>

            <template
              v-if="
                (showPostalCode !== false && addr?.postalCode) ||
                (showCity !== false && addr?.city)
              "
            >
              <div class="propeller-address-card__city text-muted-foreground">
                {{
                  [
                    showPostalCode !== false ? addr?.postalCode : null,
                    showCity !== false ? addr?.city : null,
                  ]
                    .filter(Boolean)
                    .join(" ")
                }}
              </div>
            </template>
          </slot>

          <slot
            v-if="showCountry !== false && addr?.country"
            name="country"
            :address="addr"
            :countryName="getCountryName(addr?.country)"
          >
            <div class="propeller-address-card__country text-muted-foreground">
              {{ getCountryName(addr?.country) }}
            </div>
          </slot>

          <template v-if="!!showEmail && addr?.email">
            <div class="propeller-address-card__email text-muted-foreground">
              {{ addr?.email }}
            </div>
          </template>

          <template v-if="!!showPhone && addr?.phone">
            <div class="propeller-address-card__phone text-muted-foreground">
              {{ addr?.phone }}
            </div>
          </template>

          <slot
            v-if="showDefaultBadge === true && addr?.isDefault === 'Y'"
            name="defaultBadge"
            :address="addr"
            :isDefault="true"
            :addressType="addr?.type"
          >
            <div class="mt-2">
              <span
                class="propeller-address-card__default-badge bg-secondary/10 text-secondary text-xs px-2 py-1 rounded-full"
              >
                Default {{ addr?.type }} Address
              </span>
            </div>
          </slot>
        </div>
        <slot
          v-if="enableActions !== false"
          name="actions"
          :address="addr"
          :onEdit="openEditModal"
          :onDelete="() => { showDeleteConfirm = true; }"
          :onSetDefault="handleSetDefault"
          :saving="saving"
        >
          <div
            class="propeller-address-card__actions mt-4 pt-4 border-t border-border-subtle flex flex-wrap gap-2"
          >
            <template v-if="enableEdit !== false">
              <button
                class="propeller-address-card__edit-btn text-primary hover:text-primary/80 text-sm font-medium"
                @click="async (event) => openEditModal()"
              >
                {{ getLabel("edit", "Edit") }}
              </button>
            </template>

            <template v-if="enableDelete !== false">
              <button
                class="propeller-address-card__delete-btn text-muted-foreground hover:text-foreground text-sm font-medium"
                @click="
                  async (event) => {
                    showDeleteConfirm = true;
                  }
                "
              >
                {{ getLabel("delete", "Delete") }}
              </button>
            </template>

            <template
              v-if="enableSetDefault !== false && addr?.isDefault !== 'Y'"
            >
              <button
                class="propeller-address-card__default-btn text-primary hover:text-primary/80 text-sm font-medium ml-auto"
                @click="async (event) => handleSetDefault()"
              >
                {{ getLabel("setDefault", "Set Default") }}
              </button>
            </template>
          </div>
        </slot>
      </div>
    </template>

    <template v-if="inline && showEditModal">
      <div
        class="propeller-address-card__form bg-card p-6 rounded-[var(--radius-container)] border"
      >
        <form @submit="async (e) => handleSaveEdit(e)">
          <template v-if="!!formTitle">
            <h3 class="text-xl font-bold mb-4">{{ formTitle }}</h3>
          </template>

          <div class="space-y-4">
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium mb-1">{{
                  getLabel("gender", "Gender")
                }}</label
                ><select
                  class="propeller-address-card__input w-full h-10 px-3 rounded-[var(--radius-control)] border border-input bg-card"
                  :value="editGender"
                  @change="
                    async (e) => {
                      editGender = (
                        e.target as HTMLInputElement | HTMLSelectElement
                      ).value as Gender;
                    }
                  "
                >
                  <option value="M">
                    {{ getLabel("genderMale", "Male") }}
                  </option>
                  <option value="F">
                    {{ getLabel("genderFemale", "Female") }}
                  </option>
                  <option value="U">
                    {{ getLabel("genderOther", "Other") }}
                  </option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium mb-1">{{
                  getLabel("company", "Company")
                }}</label
                ><input
                  type="text"
                  class="propeller-address-card__input w-full h-10 px-3 rounded-[var(--radius-control)] border border-input"
                  :value="editCompany"
                  @change="
                    async (e) => {
                      editCompany = (
                        e.target as HTMLInputElement | HTMLSelectElement
                      ).value;
                    }
                  "
                />
              </div>
            </div>
            <div class="grid grid-cols-3 gap-4">
              <div>
                <label class="block text-sm font-medium mb-1"
                  >{{ getLabel("firstName", "First Name") }} *</label
                ><input
                  type="text"
                  class="propeller-address-card__input w-full h-10 px-3 rounded-[var(--radius-control)] border border-input"
                  :value="editFirstName"
                  @change="
                    async (e) => {
                      editFirstName = (
                        e.target as HTMLInputElement | HTMLSelectElement
                      ).value;
                    }
                  "
                  :required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium mb-1">{{
                  getLabel("middleName", "Middle Name")
                }}</label
                ><input
                  type="text"
                  class="propeller-address-card__input w-full h-10 px-3 rounded-[var(--radius-control)] border border-input"
                  :value="editMiddleName"
                  @change="
                    async (e) => {
                      editMiddleName = (
                        e.target as HTMLInputElement | HTMLSelectElement
                      ).value;
                    }
                  "
                />
              </div>
              <div>
                <label class="block text-sm font-medium mb-1"
                  >{{ getLabel("lastName", "Last Name") }} *</label
                ><input
                  type="text"
                  class="propeller-address-card__input w-full h-10 px-3 rounded-[var(--radius-control)] border border-input"
                  :value="editLastName"
                  @change="
                    async (e) => {
                      editLastName = (
                        e.target as HTMLInputElement | HTMLSelectElement
                      ).value;
                    }
                  "
                  :required="true"
                />
              </div>
            </div>
            <div class="grid grid-cols-12 gap-4">
              <div class="col-span-8">
                <label class="block text-sm font-medium mb-1"
                  >{{ getLabel("street", "Street") }} *</label
                ><input
                  type="text"
                  class="propeller-address-card__input w-full h-10 px-3 rounded-[var(--radius-control)] border border-input"
                  :value="editStreet"
                  @change="
                    async (e) => {
                      editStreet = (
                        e.target as HTMLInputElement | HTMLSelectElement
                      ).value;
                    }
                  "
                  :required="true"
                />
              </div>
              <div class="col-span-2">
                <label class="block text-sm font-medium mb-1"
                  >{{ getLabel("number", "Number") }} *</label
                ><input
                  type="text"
                  class="propeller-address-card__input w-full h-10 px-3 rounded-[var(--radius-control)] border border-input"
                  :value="editNumber"
                  @change="
                    async (e) => {
                      editNumber = (
                        e.target as HTMLInputElement | HTMLSelectElement
                      ).value;
                    }
                  "
                  :required="true"
                />
              </div>
              <div class="col-span-2">
                <label class="block text-sm font-medium mb-1">{{
                  getLabel("numberExtension", "Ext")
                }}</label
                ><input
                  type="text"
                  class="propeller-address-card__input w-full h-10 px-3 rounded-[var(--radius-control)] border border-input"
                  :value="editNumberExtension"
                  @change="
                    async (e) => {
                      editNumberExtension = (
                        e.target as HTMLInputElement | HTMLSelectElement
                      ).value;
                    }
                  "
                />
              </div>
            </div>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium mb-1"
                  >{{ getLabel("postalCode", "Postal Code") }} *</label
                ><input
                  type="text"
                  class="propeller-address-card__input w-full h-10 px-3 rounded-[var(--radius-control)] border border-input"
                  :value="editPostalCode"
                  @change="
                    async (e) => {
                      editPostalCode = (
                        e.target as HTMLInputElement | HTMLSelectElement
                      ).value;
                    }
                  "
                  :required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium mb-1"
                  >{{ getLabel("city", "City") }} *</label
                ><input
                  type="text"
                  class="propeller-address-card__input w-full h-10 px-3 rounded-[var(--radius-control)] border border-input"
                  :value="editCity"
                  @change="
                    async (e) => {
                      editCity = (
                        e.target as HTMLInputElement | HTMLSelectElement
                      ).value;
                    }
                  "
                  :required="true"
                />
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium mb-1"
                >{{ getLabel("country", "Country") }} *</label
              ><select
                class="w-full h-10 px-3 rounded-[var(--radius-control)] border border-input bg-card"
                :value="editCountry"
                @change="
                  async (e) => {
                    editCountry = (
                      e.target as HTMLInputElement | HTMLSelectElement
                    ).value;
                  }
                "
                :required="true"
              >
                <option value="">
                  {{ getLabel("selectCountry", "Select country") }}
                </option>
                <template :key="c.code" v-for="(c, index) in countries || []">
                  <option :value="c.code">{{ c.name }}</option>
                </template>
              </select>
            </div>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium mb-1"
                  >{{ getLabel("email", "Email") }}</label
                ><input
                  type="email"
                  class="propeller-address-card__input w-full h-10 px-3 rounded-[var(--radius-control)] border border-input"
                  :value="editEmail"
                  @change="
                    async (e) => {
                      editEmail = (
                        e.target as HTMLInputElement | HTMLSelectElement
                      ).value;
                    }
                  "
                  :pattern="EMAIL_PATTERN"
                />
              </div>
              <div>
                <label class="block text-sm font-medium mb-1">{{
                  getLabel("phone", "Phone")
                }}</label
                ><input
                  type="tel"
                  class="propeller-address-card__input w-full h-10 px-3 rounded-[var(--radius-control)] border border-input"
                  :value="editPhone"
                  @change="
                    async (e) => {
                      editPhone = (
                        e.target as HTMLInputElement | HTMLSelectElement
                      ).value;
                    }
                  "
                />
              </div>
            </div>
            <template v-if="!!showIcp">
              <div class="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="icp-inline"
                  class="propeller-address-card__checkbox h-4 w-4 rounded border-input text-primary focus:ring-primary"
                  :checked="editIcp === YesNo.Y"
                  @change="
                    async (e) => {
                      editIcp = (e.target as HTMLInputElement).checked
                        ? YesNo.Y
                        : YesNo.N;
                    }
                  "
                /><label for="icp-inline" class="text-sm font-medium">{{
                  getLabel("icp", "ICP/ICS (Intra-Community Supply)")
                }}</label>
              </div>
            </template>
          </div>
          <div class="flex justify-end gap-3 pt-4 mt-4 border-t">
            <template v-if="!isNew">
              <button
                type="button"
                class="propeller-address-card__cancel-btn px-4 py-2 border rounded hover:bg-surface-hover disabled:opacity-50"
                @click="async (event) => closeEditModal()"
                :disabled="saving"
              >
                {{ getLabel("cancel", "Cancel") }}
              </button>
            </template>

            <template v-if="isNew && !!onCancel">
              <button
                type="button"
                class="propeller-address-card__cancel-btn px-4 py-2 border rounded hover:bg-surface-hover disabled:opacity-50"
                @click="async (event) => closeEditModal()"
                :disabled="saving"
              >
                {{ getLabel("cancel", "Cancel") }}
              </button>
            </template>

            <button
              type="submit"
              class="propeller-address-card__submit-btn px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 disabled:opacity-50"
              :disabled="saving"
            >
              <template v-if="saving">
                {{ getLabel("saving", "Saving...") }}
              </template>

              <template v-else>
                {{ getLabel("save", "Save") }}
              </template>
            </button>
          </div>
        </form>
      </div>
    </template>

    <template v-if="!inline && showEditModal">
      <div
        class="propeller-address-card__modal fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto py-10"
      >
        <div
          class="propeller-address-card__modal-content bg-card p-6 rounded-[var(--radius-container)] max-w-2xl w-full mx-4 shadow-xl"
        >
          <form @submit="async (e) => handleSaveEdit(e)">
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-xl font-bold">{{ formTitle }}</h3>
              <button
                type="button"
                class="propeller-address-card__modal-close text-muted-foreground hover:text-muted-foreground text-xl leading-none"
                @click="async (event) => closeEditModal()"
              >
                &times;
              </button>
            </div>
            <div class="space-y-4">
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium mb-1">{{
                    getLabel("gender", "Gender")
                  }}</label
                  ><select
                    class="propeller-address-card__input w-full h-10 px-3 rounded-[var(--radius-control)] border border-input bg-card"
                    :value="editGender"
                    @change="
                      async (e) => {
                        editGender = (
                          e.target as HTMLInputElement | HTMLSelectElement
                        ).value as Gender;
                      }
                    "
                  >
                    <option value="M">
                      {{ getLabel("genderMale", "Male") }}
                    </option>
                    <option value="F">
                      {{ getLabel("genderFemale", "Female") }}
                    </option>
                    <option value="U">
                      {{ getLabel("genderOther", "Other") }}
                    </option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium mb-1">{{
                    getLabel("company", "Company")
                  }}</label
                  ><input
                    type="text"
                    class="propeller-address-card__input w-full h-10 px-3 rounded-[var(--radius-control)] border border-input"
                    :value="editCompany"
                    @change="
                      async (e) => {
                        editCompany = (
                          e.target as HTMLInputElement | HTMLSelectElement
                        ).value;
                      }
                    "
                  />
                </div>
              </div>
              <div class="grid grid-cols-3 gap-4">
                <div>
                  <label class="block text-sm font-medium mb-1"
                    >{{ getLabel("firstName", "First Name") }} *</label
                  ><input
                    type="text"
                    class="propeller-address-card__input w-full h-10 px-3 rounded-[var(--radius-control)] border border-input"
                    :value="editFirstName"
                    @change="
                      async (e) => {
                        editFirstName = (
                          e.target as HTMLInputElement | HTMLSelectElement
                        ).value;
                      }
                    "
                    :required="true"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium mb-1">{{
                    getLabel("middleName", "Middle Name")
                  }}</label
                  ><input
                    type="text"
                    class="propeller-address-card__input w-full h-10 px-3 rounded-[var(--radius-control)] border border-input"
                    :value="editMiddleName"
                    @change="
                      async (e) => {
                        editMiddleName = (
                          e.target as HTMLInputElement | HTMLSelectElement
                        ).value;
                      }
                    "
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium mb-1"
                    >{{ getLabel("lastName", "Last Name") }} *</label
                  ><input
                    type="text"
                    class="propeller-address-card__input w-full h-10 px-3 rounded-[var(--radius-control)] border border-input"
                    :value="editLastName"
                    @change="
                      async (e) => {
                        editLastName = (
                          e.target as HTMLInputElement | HTMLSelectElement
                        ).value;
                      }
                    "
                    :required="true"
                  />
                </div>
              </div>
              <div class="grid grid-cols-12 gap-4">
                <div class="col-span-8">
                  <label class="block text-sm font-medium mb-1"
                    >{{ getLabel("street", "Street") }} *</label
                  ><input
                    type="text"
                    class="propeller-address-card__input w-full h-10 px-3 rounded-[var(--radius-control)] border border-input"
                    :value="editStreet"
                    @change="
                      async (e) => {
                        editStreet = (
                          e.target as HTMLInputElement | HTMLSelectElement
                        ).value;
                      }
                    "
                    :required="true"
                  />
                </div>
                <div class="col-span-2">
                  <label class="block text-sm font-medium mb-1"
                    >{{ getLabel("number", "Number") }} *</label
                  ><input
                    type="text"
                    class="propeller-address-card__input w-full h-10 px-3 rounded-[var(--radius-control)] border border-input"
                    :value="editNumber"
                    @change="
                      async (e) => {
                        editNumber = (
                          e.target as HTMLInputElement | HTMLSelectElement
                        ).value;
                      }
                    "
                    :required="true"
                  />
                </div>
                <div class="col-span-2">
                  <label class="block text-sm font-medium mb-1">{{
                    getLabel("numberExtension", "Ext")
                  }}</label
                  ><input
                    type="text"
                    class="propeller-address-card__input w-full h-10 px-3 rounded-[var(--radius-control)] border border-input"
                    :value="editNumberExtension"
                    @change="
                      async (e) => {
                        editNumberExtension = (
                          e.target as HTMLInputElement | HTMLSelectElement
                        ).value;
                      }
                    "
                  />
                </div>
              </div>
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium mb-1"
                    >{{ getLabel("postalCode", "Postal Code") }} *</label
                  ><input
                    type="text"
                    class="propeller-address-card__input w-full h-10 px-3 rounded-[var(--radius-control)] border border-input"
                    :value="editPostalCode"
                    @change="
                      async (e) => {
                        editPostalCode = (
                          e.target as HTMLInputElement | HTMLSelectElement
                        ).value;
                      }
                    "
                    :required="true"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium mb-1"
                    >{{ getLabel("city", "City") }} *</label
                  ><input
                    type="text"
                    class="propeller-address-card__input w-full h-10 px-3 rounded-[var(--radius-control)] border border-input"
                    :value="editCity"
                    @change="
                      async (e) => {
                        editCity = (
                          e.target as HTMLInputElement | HTMLSelectElement
                        ).value;
                      }
                    "
                    :required="true"
                  />
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium mb-1"
                  >{{ getLabel("country", "Country") }} *</label
                ><select
                  class="propeller-address-card__input w-full h-10 px-3 rounded-[var(--radius-control)] border border-input bg-card"
                  :value="editCountry"
                  @change="
                    async (e) => {
                      editCountry = (
                        e.target as HTMLInputElement | HTMLSelectElement
                      ).value;
                    }
                  "
                  :required="true"
                >
                  <option value="">
                    {{ getLabel("selectCountry", "Select country") }}
                  </option>
                  <template :key="c.code" v-for="(c, index) in countries || []">
                    <option :value="c.code">{{ c.name }}</option>
                  </template>
                </select>
              </div>
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium mb-1"
                    >{{ getLabel("email", "Email") }}</label
                  ><input
                    type="email"
                    class="propeller-address-card__input w-full h-10 px-3 rounded-[var(--radius-control)] border border-input"
                    :value="editEmail"
                    @change="
                      async (e) => {
                        editEmail = (
                          e.target as HTMLInputElement | HTMLSelectElement
                        ).value;
                      }
                    "
                    :pattern="EMAIL_PATTERN"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium mb-1">{{
                    getLabel("phone", "Phone")
                  }}</label
                  ><input
                    type="tel"
                    class="propeller-address-card__input w-full h-10 px-3 rounded-[var(--radius-control)] border border-input"
                    :value="editPhone"
                    @change="
                      async (e) => {
                        editPhone = (
                          e.target as HTMLInputElement | HTMLSelectElement
                        ).value;
                      }
                    "
                  />
                </div>
              </div>
              <template v-if="!!showIcp">
                <div class="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="icp-modal"
                    class="propeller-address-card__checkbox h-4 w-4 rounded border-input text-primary focus:ring-primary"
                    :checked="editIcp === YesNo.Y"
                    @change="
                      async (e) => {
                        editIcp = (e.target as HTMLInputElement).checked
                          ? YesNo.Y
                          : YesNo.N;
                      }
                    "
                  /><label for="icp-modal" class="text-sm font-medium">{{
                    getLabel("icp", "ICP/ICS (Intra-Community Supply)")
                  }}</label>
                </div>
              </template>
            </div>
            <div class="flex justify-end gap-3 pt-4 mt-4 border-t">
              <button
                type="button"
                class="propeller-address-card__cancel-btn px-4 py-2 border rounded hover:bg-surface-hover disabled:opacity-50"
                @click="async (event) => closeEditModal()"
                :disabled="saving"
              >
                {{ getLabel("cancel", "Cancel") }}</button
              ><button
                type="submit"
                class="propeller-address-card__submit-btn px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 disabled:opacity-50"
                :disabled="saving"
              >
                <template v-if="saving">
                  {{ getLabel("saving", "Saving...") }}
                </template>

                <template v-else>
                  {{ getLabel("save", "Save") }}
                </template>
              </button>
            </div>
          </form>
        </div>
      </div>
    </template>

    <template v-if="showDeleteConfirm">
      <div
        class="propeller-address-card__delete-modal fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      >
        <div
          class="propeller-address-card__delete-modal-content bg-card p-6 rounded-[var(--radius-container)] max-w-sm w-full mx-4"
        >
          <h3 class="text-xl font-bold mb-4">
            {{ getLabel("confirmDeleteTitle", "Confirm Delete") }}
          </h3>
          <p
            class="propeller-address-card__delete-message mb-6 text-muted-foreground"
          >
            {{
              getLabel(
                "confirmDeleteMessage",
                "Are you sure you want to delete this address?",
              )
            }}
          </p>
          <div class="flex justify-end gap-4">
            <button
              class="propeller-address-card__cancel-btn px-4 py-2 border rounded hover:bg-surface-hover"
              @click="
                async (event) => {
                  showDeleteConfirm = false;
                }
              "
            >
              {{ getLabel("cancel", "Cancel") }}</button
            ><button
              class="propeller-address-card__confirm-btn px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/80"
              @click="async (event) => confirmDelete()"
            >
              {{ getLabel("delete", "Delete") }}
            </button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";

import { Address, CartAddress, Gender, GraphQLClient, OrderAddress, WarehouseAddress, YesNo } from "@propeller-commerce/propeller-sdk-v2";
import { getLabel as _getLabel } from '@propeller-commerce/propeller-v2-core-ui';
import { getCountryName as _getCountryName } from '@propeller-commerce/propeller-v2-core-ui';

/**
 * Email pattern requiring a dotted top-level domain.
 *
 * `type="email"` alone accepts dotless domains such as `aa@gg` — valid per the
 * HTML5 spec (intranet hosts like `user@localhost`), but not a deliverable
 * address here, and rejected by the API. Email is optional on an address; this
 * only constrains the value once something has been typed.
 */
const EMAIL_PATTERN = '[^@\\s]+@[^@\\s.]+(?:\\.[^@\\s.]+)*\\.[A-Za-z]{2,}';

export interface AddressCardProps {
  /** GraphQL client for the Propeller SDK (only needed when editing) */
  graphqlClient?: GraphQLClient;

  /** The address to display (Address | CartAddress | WarehouseAddress | OrderAddress) */
  address: Address | CartAddress | WarehouseAddress | OrderAddress | null;

  /** Display company name @default true */
  showCompanyName?: boolean;

  /** Display salutation (Mr./Mrs.) @default true */
  showSalutation?: boolean;

  /** Display full name @default true */
  showFullName?: boolean;

  /** Display street @default true */
  showStreet?: boolean;

  /** Display house number and extension @default true */
  showNumberExtension?: boolean;

  /** Display postal code @default true */
  showPostalCode?: boolean;

  /** Display city @default true */
  showCity?: boolean;

  /** Display country name @default true */
  showCountry?: boolean;

  /** Display email @default false */
  showEmail?: boolean;

  /** Display phone @default false */
  showPhone?: boolean;

  /** Display action buttons (edit, delete, set default) @default true */
  enableActions?: boolean;

  /** Display Edit button @default true */
  enableEdit?: boolean;

  /** Display Delete button @default true */
  enableDelete?: boolean;

  /** Display Set Default button @default true */
  enableSetDefault?: boolean;

  /** Display the "Default ... Address" badge @default false */
  showDefaultBadge?: boolean;

  /**
   * Display the address-type badge (invoice/delivery chip) @default true.
   * Set to `false` where the surrounding heading already names the address
   * type, to avoid a redundant raw-looking chip.
   */
  showTypeBadge?: boolean;

  /** Called when address is edited; receives the updated address object */
  onEdit?: (address: Address) => void | Promise<void>;

  /** Called after address edit completes */
  afterEdit?: (address: Address) => void | Promise<void>;

  /** Called when address is deleted; receives the address ID */
  onDelete?: (addressId: Address) => void;

  /** Called after address deletion completes */
  afterDelete?: (addressId: Address) => void;

  /** Called when address is set as default */
  onSetDefault?: (address: Address) => void;

  /** Called after address is set as default */
  afterSetDefault?: (address: Address) => void;

  /** List of countries for the country dropdown [{code: 'NL', name: 'Netherlands'}, ...] */
  countries?: {
    code: string;
    name: string;
  }[];

  /** When true, renders in "new address" mode: auto-opens the edit form, hides the card body */
  isNew?: boolean;

  /** Called when the form is cancelled in new mode */
  onCancel?: () => void;

  /** When true, renders the form inline instead of in a modal overlay. @default false */
  inline?: boolean;

  /** Address type for new addresses (e.g., 'DELIVERY', 'INVOICE'). Used when creating, not editing. */
  addressType?: string;

  /** Show ICP/ICS (intra-community supply) checkbox. @default false */
  showIcp?: boolean;

  /** Title for the form or section */
  title?: string;

  /** Labels for form fields and buttons */
  labels?: Record<string, string>;

  /** Called before save starts */
  beforeSave?: () => void;
}
interface AddressCardState {
  showEditModal: boolean;
  showDeleteConfirm: boolean;
  localAddress: any;
  editCompany: string;
  editGender: Gender;
  editFirstName: string;
  editMiddleName: string;
  editLastName: string;
  editStreet: string;
  editNumber: string;
  editNumberExtension: string;
  editPostalCode: string;
  editCity: string;
  editCountry: string;
  editEmail: string;
  editPhone: string;
  editNotes: string;
  editIcp: YesNo;
  saving: boolean;
  getLabel: (key: string, fallback: string) => string;
  getCountryName: (code: string) => string;
  addr: any;
  showCard: boolean;
  formTitle: string;
  openEditModal: () => void;
  handleSaveEdit: (e: any) => Promise<void>;
  confirmDelete: () => void;
  handleSetDefault: () => void;
  closeEditModal: () => void;
}

const props = withDefaults(defineProps<AddressCardProps>(), {
  showCompanyName: true,
  showSalutation: true,
  showFullName: true,
  showStreet: true,
  showNumberExtension: true,
  showPostalCode: true,
  showCity: true,
  showCountry: true,
  showEmail: false,
  showPhone: false,
  enableActions: true,
  enableEdit: true,
  enableDelete: true,
  enableSetDefault: true,
  showDefaultBadge: false,
  isNew: false,
  inline: false,
  showIcp: false,
});
const showEditModal = ref<AddressCardState["showEditModal"]>(false);
const showDeleteConfirm = ref<AddressCardState["showDeleteConfirm"]>(false);
const saving = ref<AddressCardState["saving"]>(false);
const localAddress = ref<AddressCardState["localAddress"]>(null);
const editCompany = ref<AddressCardState["editCompany"]>("");
const editGender = ref<AddressCardState["editGender"]>(Gender.U);
const editFirstName = ref<AddressCardState["editFirstName"]>("");
const editMiddleName = ref<AddressCardState["editMiddleName"]>("");
const editLastName = ref<AddressCardState["editLastName"]>("");
const editStreet = ref<AddressCardState["editStreet"]>("");
const editNumber = ref<AddressCardState["editNumber"]>("");
const editNumberExtension = ref<AddressCardState["editNumberExtension"]>("");
const editPostalCode = ref<AddressCardState["editPostalCode"]>("");
const editCity = ref<AddressCardState["editCity"]>("");
const editCountry = ref<AddressCardState["editCountry"]>("");
const editEmail = ref<AddressCardState["editEmail"]>("");
const editPhone = ref<AddressCardState["editPhone"]>("");
const editNotes = ref<AddressCardState["editNotes"]>("");
const editIcp = ref<AddressCardState["editIcp"]>(YesNo.N);

onMounted(() => {
  if (props.isNew || (props.inline && !props.address)) {
    openEditModal();
  }
});

const addr = computed(() => {
  return localAddress.value || props.address;
});
const showCard = computed(() => {
  if (props.isNew) return false;
  if (props.inline && !props.address) return false;
  return true;
});
const formTitle = computed(() => {
  if (props.title) return props.title;
  if (props.isNew) return getLabel("newTitle", "New Address");
  return getLabel("editTitle", "Edit Address");
});

watch(
  () => [props.address],
  () => {
    localAddress.value = null;
  },
  { immediate: true },
);
function getLabel(
  key: string,
  fallback: string,
): ReturnType<AddressCardState["getLabel"]> {
  return _getLabel(props.labels, key, fallback);
}
function getCountryName(
  code: string,
): ReturnType<AddressCardState["getCountryName"]> {
  return _getCountryName(code, props.countries);
}
function openEditModal(): ReturnType<AddressCardState["openEditModal"]> {
  const a = addr.value;
  editCompany.value = a?.company || "";
  editGender.value = a?.gender || "M";
  editFirstName.value = a?.firstName || "";
  editMiddleName.value = a?.middleName || "";
  editLastName.value = a?.lastName || "";
  editStreet.value = a?.street || "";
  editNumber.value = a?.number || "";
  editNumberExtension.value = a?.numberExtension || "";
  editPostalCode.value = a?.postalCode || "";
  editCity.value = a?.city || "";
  editCountry.value = a?.country || "";
  editEmail.value = a?.email || "";
  editPhone.value = a?.phone || "";
  editNotes.value = a?.notes || "";
  editIcp.value = a?.icp || YesNo.N;
  showEditModal.value = true;
}
async function handleSaveEdit(
  e: any,
): ReturnType<AddressCardState["handleSaveEdit"]> {
  e.preventDefault();
  if (saving.value) return;
  saving.value = true;
  if (props.beforeSave) {
    props.beforeSave();
  }
  const editedAddress = {
    id: addr.value?.id,
    type: addr.value?.type || props.addressType || "",
    isDefault: addr.value?.isDefault,
    company: editCompany.value,
    gender: editGender.value,
    firstName: editFirstName.value,
    middleName: editMiddleName.value,
    lastName: editLastName.value,
    street: editStreet.value,
    number: editNumber.value,
    numberExtension: editNumberExtension.value,
    postalCode: editPostalCode.value,
    city: editCity.value,
    country: editCountry.value,
    email: editEmail.value,
    phone: editPhone.value,
    notes: editNotes.value,
    icp: editIcp.value as YesNo,
  } as unknown as Address;
  localAddress.value = editedAddress;
  try {
    if (props.onEdit) {
      await props.onEdit(editedAddress);
    }
    showEditModal.value = false;
    if (props.afterEdit) {
      await props.afterEdit(editedAddress);
    }
  } finally {
    saving.value = false;
  }
}
function confirmDelete(): ReturnType<AddressCardState["confirmDelete"]> {
  const id = addr.value?.id;
  if (id != null) {
    if (props.onDelete) {
      props.onDelete(addr.value);
    }
    showDeleteConfirm.value = false;
    if (props.afterDelete) {
      props.afterDelete(addr.value);
    }
  } else {
    showDeleteConfirm.value = false;
  }
}
function handleSetDefault(): ReturnType<AddressCardState["handleSetDefault"]> {
  if (props.onSetDefault) {
    props.onSetDefault(addr.value);
  }
  if (props.afterSetDefault) {
    props.afterSetDefault(addr.value);
  }
}
function closeEditModal(): ReturnType<AddressCardState["closeEditModal"]> {
  showEditModal.value = false;
  if (props.isNew && props.onCancel) {
    props.onCancel();
  }
}
</script>
